import React, { useState } from 'react';
import { dashApi, formatAmount } from '../../../api/dashApi';

// ─── Constants ────────────────────────────────────────────────────────────────
const MONTHS = [
  { value: 1,  label: 'January'   },
  { value: 2,  label: 'February'  },
  { value: 3,  label: 'March'     },
  { value: 4,  label: 'April'     },
  { value: 5,  label: 'May'       },
  { value: 6,  label: 'June'      },
  { value: 7,  label: 'July'      },
  { value: 8,  label: 'August'    },
  { value: 9,  label: 'September' },
  { value: 10, label: 'October'   },
  { value: 11, label: 'November'  },
  { value: 12, label: 'December'  },
];

const YEARS = [new Date().getFullYear() - 1, new Date().getFullYear()];

// Rolling 3 months before currMonth (inclusive of prev months only)
function getRollingThreeMonths(currMonth, currYear) {
  const result = [];
  for (let i = 1; i <= 3; i++) {
    let m = currMonth - i;
    let y = currYear;
    if (m <= 0) { m += 12; y -= 1; }
    result.push({ month: m, year: y, label: `${MONTHS[m - 1].label} ${y}` });
  }
  return result.reverse();
}

/** Sort rows by year DESC then month DESC — most recent first */
function sortDesc(arr) {
  if (!Array.isArray(arr)) return arr ? [arr] : [];
  return [...arr].sort((a, b) => {
    const av = (a.year || 0) * 100 + (a.month || 0);
    const bv = (b.year || 0) * 100 + (b.month || 0);
    return bv - av;
  });
}

// ─── Card components ──────────────────────────────────────────────────────────
function CardShell({ tag, children }) {
  return (
    <div className="bg-white border border-primary/20 rounded-xl shadow-sm p-4 flex flex-col gap-3 hover:shadow-md transition-shadow">
      <div className="border-b border-primary/10 pb-2">
        <h2 className="text-sm font-bold text-primary">{tag}</h2>
      </div>
      {children}
    </div>
  );
}

function BaseTable({ headers, rows }) {
  if (!rows || rows.length === 0)
    return <p className="text-xs text-gray-400 text-center py-3">No data.</p>;
  return (
    <div className="overflow-x-auto rounded-md">
      <table className="w-full text-xs border border-primary/10">
        <thead>
          <tr className="bg-primary/5 text-primary">
            {headers.map(h => (
              <th key={h} className="px-3 py-2 border border-primary/10 text-center font-semibold whitespace-nowrap">{h}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, i) => (
            <tr key={i} className="hover:bg-primary/5 transition-colors">
              {headers.map((h, j) => (
                <td key={j} className="px-3 py-2 border border-primary/10 text-center text-gray-700">{row[j]}</td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// Standard card: Year | Month | Amount | Count
function AnalyzeCard({ tag, data }) {
  const rows = sortDesc(data).map(r => [
    r.year,
    MONTHS[(r.month || 0) - 1]?.label || '—',
    formatAmount(r.amount),
    r.count ?? '—',
  ]);
  return (
    <CardShell tag={tag}>
      <BaseTable headers={['Year', 'Month', 'Amount', 'Count']} rows={rows} />
    </CardShell>
  );
}

// Money Transfer / Recharge: Year | Month | Amount | Money Transfer Fee | Count
function MoneyTransferCard({ tag, data }) {
  const rows = sortDesc(data).map(r => [
    r.year,
    MONTHS[(r.month || 0) - 1]?.label || '—',
    formatAmount(r.amount),
    formatAmount(r.moneyTransferFee || 0),
    r.count ?? '—',
  ]);
  return (
    <CardShell tag={tag}>
      <BaseTable headers={['Year', 'Month', 'Amount', 'Money Transfer Fee', 'Count']} rows={rows} />
    </CardShell>
  );
}

// Fleet Pool: Year | Month | Transaction Category | Amount | Count
function PoolFleetCard({ tag, data }) {
  const rows = sortDesc(data).map(r => [
    r.year,
    MONTHS[(r.month || 0) - 1]?.label || '—',
    r.transactionCategory || r.TransactionCategory || '—',
    formatAmount(r.amount),
    r.count ?? '—',
  ]);
  return (
    <CardShell tag={tag}>
      <BaseTable headers={['Year', 'Month', 'Transaction Category', 'Amount', 'Count']} rows={rows} />
    </CardShell>
  );
}

// Add Money PG: Year | Month | Merchant | Amount | Count
function AddMoneyPGCard({ tag, data }) {
  const rows = sortDesc(data).map(r => [
    r.year,
    MONTHS[(r.month || 0) - 1]?.label || '—',
    r.transactionLoadMoneyMerchant || r.TransactionLoadMoneyMerchant || '—',
    formatAmount(r.amount),
    r.count ?? '—',
  ]);
  return (
    <CardShell tag={tag}>
      <BaseTable headers={['Year', 'Month', 'Merchant', 'Amount', 'Count']} rows={rows} />
    </CardShell>
  );
}

// Shimmer placeholder
function Shimmer() {
  return (
    <>
      {Array.from({ length: 6 }).map((_, i) => (
        <div key={i} className="bg-white border border-gray-100 rounded-xl p-4 animate-pulse">
          <div className="h-4 bg-gray-200 rounded w-1/3 mb-4" />
          <div className="space-y-2">
            {[1, 2, 3].map(j => <div key={j} className="h-3 bg-gray-100 rounded" />)}
          </div>
        </div>
      ))}
    </>
  );
}

// ─── Main Page ────────────────────────────────────────────────────────────────
export default function AnalyzePage() {
  const now = new Date();
  const thisMonth = now.getMonth() + 1;
  const thisYear  = now.getFullYear();

  const defaultPrev = getRollingThreeMonths(thisMonth, thisYear);

  const [year,      setYear]      = useState(thisYear);
  const [currMonth, setCurrMonth] = useState(thisMonth);
  const [prevMonth, setPrevMonth] = useState(defaultPrev[defaultPrev.length - 1].month);
  const [prevYear,  setPrevYear]  = useState(defaultPrev[defaultPrev.length - 1].year);
  const [data,      setData]      = useState(null);
  const [loading,   setLoading]   = useState(false);
  const [error,     setError]     = useState('');
  const [fetchedAt, setFetchedAt] = useState(null);

  const prevMonthOptions = getRollingThreeMonths(currMonth, year);

  const handleCurrMonthChange = (val) => {
    const m = Number(val);
    setCurrMonth(m);
    const opts = getRollingThreeMonths(m, year);
    if (!opts.some(o => o.month === prevMonth && o.year === prevYear)) {
      setPrevMonth(opts[opts.length - 1].month);
      setPrevYear(opts[opts.length - 1].year);
    }
  };

  const handlePrevChange = (key) => {
    const [m, y] = key.split('-').map(Number);
    setPrevMonth(m);
    setPrevYear(y);
  };

  const fetchData = async () => {
    setError('');
    setLoading(true);
    setData(null);
    try {
      const res = await dashApi.analyzeMonthRange(currMonth, prevMonth, year, prevYear);
      setData(res.data);
      setFetchedAt(res.data?.cachedAt || new Date().toLocaleString('en-IN', { timeZone: 'Asia/Kolkata' }));
    } catch (err) {
      setError('Failed to fetch analysis data. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 p-6 flex flex-col gap-6">

      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Analyze</h1>
        <p className="text-sm text-gray-500 mt-0.5">Month-over-month breakdown across all transaction categories</p>
      </div>

      {/* Filter bar */}
      <div className="bg-white rounded-xl border border-primary/20 shadow-sm p-5 flex flex-wrap items-end gap-4 justify-between">
        <div className="flex flex-wrap gap-4 items-end">

          {/* Year */}
          <div className="flex flex-col gap-1">
            <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Year</label>
            <select
              value={year}
              onChange={e => setYear(Number(e.target.value))}
              className="w-28 border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30"
            >
              {YEARS.map(y => <option key={y} value={y}>{y}</option>)}
            </select>
          </div>

          {/* Current Month */}
          <div className="flex flex-col gap-1">
            <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Current Month</label>
            <select
              value={currMonth}
              onChange={e => handleCurrMonthChange(e.target.value)}
              className="w-36 border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30"
            >
              {MONTHS.map(m => <option key={m.value} value={m.value}>{m.label}</option>)}
            </select>
          </div>

          {/* Previous Month (rolling 3) */}
          <div className="flex flex-col gap-1">
            <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Previous Month</label>
            <select
              value={`${prevMonth}-${prevYear}`}
              onChange={e => handlePrevChange(e.target.value)}
              className="w-44 border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30"
            >
              {prevMonthOptions.map(o => (
                <option key={`${o.month}-${o.year}`} value={`${o.month}-${o.year}`}>{o.label}</option>
              ))}
            </select>
          </div>
        </div>

        <div className="flex items-center gap-4">
          {fetchedAt && (
            <p className="text-xs text-gray-400 italic">Cached at: {fetchedAt}</p>
          )}
          <button
            onClick={fetchData}
            disabled={loading}
            className="px-6 py-2 bg-primary hover:bg-primary/90 text-white text-sm font-semibold rounded-lg transition-colors disabled:opacity-50 shadow-sm"
          >
            {loading ? 'Fetching…' : 'Fetch Data'}
          </button>
        </div>
      </div>

      {/* Error */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-xl p-4 text-sm text-red-600">{error}</div>
      )}

      {/* Cards grid */}
      <div className="grid gap-5" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))' }}>
        {loading ? (
          <Shimmer />
        ) : data ? (
          <>
            <AnalyzeCard     tag="Pool Account Add Money"        data={data.resPoolAccountAddMoney} />
            <AnalyzeCard     tag="Platform Fee"                   data={data.resPlatformFee} />
            <PoolFleetCard   tag="Pool Account Add Money Fleet"   data={data.resPoolAccountAddMoneyFleet} />
            <AnalyzeCard     tag="UPI Personalization"            data={data.resUpiPersonalisation} />
            <AnalyzeCard     tag="Brand Voucher"                  data={data.resCardVoucher} />
            <AnalyzeCard     tag="UPI B2B"                        data={data.resUpiB2B} />
            <AnalyzeCard     tag="UPI"                            data={data.resUpi} />
            <AnalyzeCard     tag="Card Order"                     data={data.resCardOrder} />
            <AnalyzeCard     tag="Subscription Purchase"          data={data.resSubscription} />
            <AnalyzeCard     tag="Rupay"                          data={data.resRupay} />
            <MoneyTransferCard tag="Money Transfer and CW"        data={data.resMoneyTransferAndCW} />
            <MoneyTransferCard tag="Recharge"                     data={data.resRecharge} />
            <AnalyzeCard     tag="Rupay B2B"                      data={data.resRupayB2B} />
            <AnalyzeCard     tag="Convenience Fees"               data={data.resConvienceFee} />
            <AddMoneyPGCard  tag="Add Money PG"                   data={data.resAddMoneyPG} />

            {Object.values(data).every(v => !Array.isArray(v) || v.length === 0) && (
              <div className="col-span-full text-center py-16 text-gray-400 text-sm">
                No data found for the selected period.
              </div>
            )}
          </>
        ) : (
          !error && (
            <div className="col-span-full flex flex-col items-center justify-center py-20 text-center">
              <div className="text-5xl mb-4">🔬</div>
              <p className="text-gray-500 text-sm font-medium">Select a date range and click <strong>Fetch Data</strong></p>
              <p className="text-gray-400 text-xs mt-1">Covers UPI, Card, Pool Loading, Platform Fee, Rupay, Recharge and more</p>
            </div>
          )
        )}
      </div>
    </div>
  );
}
